import React, { useState, useMemo } from 'react';
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow,
  Paper, Typography, makeStyles,
} from '@material-ui/core';
import { CPP_DATA, resolveCppBatch } from './cppStaticData';
import TimeSeriesDataModal from './TimeSeriesDataModal';
import ProcedureNumberCell from './ProcedureNumberCell';
import Pagination from '../../logisticsDashboard/components/Pagination';


const useStyles = makeStyles(() => ({
  batchLabelRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 4px 12px' },
  fieldLabel: { fontSize: '0.72rem', fontWeight: 600, color: '#333' },
  batchValue: { fontSize: '0.9rem', fontWeight: 600, color: '#111' },
  tableContainer: {
    border: '1px solid #e0e0e0', borderRadius: '4px', margin: '8px 0',
    overflowX: 'auto', width: '100%', maxWidth: '100%', boxSizing: 'border-box',
  },
  scrollArea: {
    width: '100%',
    maxHeight: 440,
    overflowY: 'auto',
    overflowX: 'auto',
  },
  headerRow: {
    backgroundColor: '#F9F9FC',
    '& th': {
      backgroundColor: '#F9F9FC',
      fontWeight: 600, fontSize: '0.82rem', color: '#333', padding: '8px 14px',
      borderBottom: '1px solid #e0e0e0', verticalAlign: 'bottom', whiteSpace: 'nowrap',
      position: 'sticky', top: 0, zIndex: 1,
    },
  },
  bodyRow: {
    '&:hover': { backgroundColor: '#f5f5f5' },
    '& td': {
      fontSize: '0.85rem', padding: '8px 14px', borderBottom: '1px solid #eeeeee',
      color: '#444', verticalAlign: 'middle', whiteSpace: 'nowrap',
    },
  },
  viewLink: { color: '#1976d2', cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'underline', '&:hover': { color: '#0d47a1' } },
  muted: { color: '#bbb' },
}));

// CPP table is driven by the workbook's CPPs summary (all process steps, with timestamps).
// "Click To View" opens the time-series chart for steps that have per-parameter data.
const CPPsTab = ({ loading, batchNumber, pageSize = 10 }) => {
  const classes = useStyles();
  const PAGE_SIZE = pageSize || 10;
  const [modalOpen, setModalOpen] = useState(false);
  const [activeKey, setActiveKey] = useState(null);
  const [page, setPage] = useState(1);

  const cppBatch = resolveCppBatch(batchNumber);
  const data = cppBatch ? CPP_DATA[cppBatch] : null;
  const rows = data?.rows || [];

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [rows, safePage, PAGE_SIZE]
  );

  if (loading) return <Box style={{ textAlign: 'center', padding: 24 }}>Loading...</Box>;

  const openPlot = (seriesKey) => { setActiveKey(seriesKey); setModalOpen(true); };

  return (
    <>
      {rows.length ? (
        <Paper className={classes.tableContainer} elevation={0}>
          <div className={classes.scrollArea}>
          <Table stickyHeader size="small" style={{ width: '100%' }}>
            <TableHead>
              <TableRow className={classes.headerRow}>
                <TableCell>Unit Name</TableCell>
                <TableCell>Phase / Step Number</TableCell>
                <TableCell>Unit Procedure</TableCell>
                <TableCell>Operation</TableCell>
                <TableCell>Phase</TableCell>
                <TableCell>Parameter</TableCell>
                <TableCell>Set Point</TableCell>
                <TableCell>Range</TableCell>
                <TableCell>Actual Value</TableCell>
                <TableCell>UoM</TableCell>
                <TableCell>Time Series Data</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map((row) => (
                <TableRow key={row.seriesKey} className={classes.bodyRow}>
                  <TableCell style={{ whiteSpace: 'normal', maxWidth: 200, wordBreak: 'break-word' }}>{row.unitName}</TableCell>
                  <TableCell><ProcedureNumberCell value={row.procedureNumber} batchNumber={batchNumber} /></TableCell>
                  <TableCell style={{ whiteSpace: 'normal', maxWidth: 180, wordBreak: 'break-word' }}>{row.unitProcedure}</TableCell>
                  <TableCell style={{ whiteSpace: 'normal', maxWidth: 160, wordBreak: 'break-word' }}>{row.operation}</TableCell>
                  <TableCell style={{ whiteSpace: 'normal', maxWidth: 300, wordBreak: 'break-word' }}>{row.phase}</TableCell>
                  <TableCell>{row.parameter}</TableCell>
                  <TableCell>{row.setPoint ?? '—'}</TableCell>
                  <TableCell>{row.range ?? '—'}</TableCell>
                  <TableCell>{row.actualValue ?? '—'}</TableCell>
                  <TableCell>{row.uom || '—'}</TableCell>
                  <TableCell>
                    {row.hasChart ? (
                      <span className={classes.viewLink} onClick={() => openPlot(row.seriesKey)}>Click To View</span>
                    ) : (
                      <span className={classes.muted}>—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          {rows.length > PAGE_SIZE && (
            <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
          )}
        </Paper>
      ) : (
        <Box style={{ textAlign: 'center', padding: 24, color: '#999' }}>
          No CPP data available for this batch.
        </Box>
      )}

      <TimeSeriesDataModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        batchNumber={cppBatch}
        initialSeriesKey={activeKey}
      />
    </>
  );
};

export default CPPsTab;
